import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { LOGOS, NAV } from "../../lib/ablemp-data";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("manifesto");
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isHome) {
      setActive("");
      return;
    }
    const ids = NAV.filter((n) => !n.route).map((n) => n.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isHome, location.pathname]);

  const handleNav = (item) => {
    setOpen(false);
    if (item.id === "associar") {
      // Direct to public sign-up wizard
      navigate("/cadastro");
      return;
    }
    if (item.route) {
      if (item.external) {
        window.open(item.route, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(item.route);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!isHome) {
      navigate("/", { state: { scrollTo: item.id } });
      return;
    }
    const el = document.getElementById(item.id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const isActive = (item) => {
    if (item.route) return location.pathname === item.route;
    return isHome && active === item.id;
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? "bg-[#0A0F14]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between gap-6">
        <button
          data-testid="header-logo-link"
          onClick={() => handleNav({ id: "manifesto" })}
          className="flex items-center gap-3 group shrink-0"
          aria-label="Início"
        >
          <img
            src={LOGOS.white}
            alt="ABLEMP"
            className="h-12 md:h-14 lg:h-16 w-auto transition-transform duration-500 group-hover:scale-105"
          />
        </button>

        <nav className="hidden xl:flex items-center gap-7 text-white/85 min-w-0">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => handleNav(item)}
              className={`editorial-link text-[12px] uppercase tracking-[0.2em] font-medium whitespace-nowrap ${
                isActive(item) ? "active text-white" : ""
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          <a
            data-testid="home-portal-link"
            href="/login"
            className="inline-flex items-center text-[12px] uppercase tracking-[0.22em] font-medium text-white/85 hover:text-[#FEDF00] px-3 py-3 border border-white/20 hover:border-[#FEDF00] transition-colors whitespace-nowrap"
          >
            Portal do Associado
          </a>
          <button
            data-testid="header-cta-associate"
            onClick={() => handleNav({ id: "associar" })}
            className="group relative inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-[#0A0F14] bg-[#FEDF00] px-5 py-3 hover:bg-white transition-colors duration-300 whitespace-nowrap"
          >
            Associe-se
            <span className="h-px w-5 bg-[#0A0F14] group-hover:w-7 transition-all" />
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden text-white p-2 shrink-0"
          aria-label="Abrir menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-[#0A0F14] border-t border-white/10 px-6 py-6 space-y-4"
        >
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`mobile-nav-link-${item.id}`}
              onClick={() => handleNav(item)}
              className="block text-white/90 text-sm uppercase tracking-[0.22em] py-1"
            >
              {item.label}
            </button>
          ))}
          <button
            data-testid="mobile-cta-associate"
            onClick={() => handleNav({ id: "associar" })}
            className="mt-3 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-[#0A0F14] bg-[#FEDF00] px-5 py-3"
          >
            Associe-se
          </button>
          <a
            data-testid="mobile-portal-link"
            href="/login"
            className="ml-2 inline-flex items-center text-[12px] uppercase tracking-[0.22em] font-medium text-white border border-white/30 px-5 py-3"
          >
            Portal
          </a>
        </div>
      )}
    </header>
  );
};

export default Header;
